import { Switch, TextField } from '@material-ui/core';
import { PageTitleGoBack } from 'components/base/title/PageTitle';
import MuiButton from 'components/common/button/MuiButton';
import MuiWrapper from 'components/common/input/MuiWrapper';
import { T } from 'components/common/text';
import CustomText from 'components/common/text/CustomText';
import CreateProjectIndicationContainer from 'containers/project/CreateProjectIndicationContainer';
import { regProjectName } from 'lib/library';
import { ICreateProjectInfoProps, ICreateProjectProp } from 'lib/types/props/projectProps';
import moment from 'moment';
import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { color } from 'styles/utils';
import CreateProjectInfo from './CreateProjectInfo';

interface CreateProjectInfoProps extends ICreateProjectProp, ICreateProjectInfoProps {
  language: string;
  indicationInfo: object | any;
  indicationFormat: object | any;
  indicationOption: object | any;
  projectNameRef: HTMLDivElement | any;
  restorationPontic: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  occlusalSurface: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  ceramicMetal: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  isValidInformationValue: boolean;
  isValidIndicationValue: boolean;
  isValidClient: boolean;
  isMultidie: {
    value: number;
    onChange: (e: any) => void;
    setValue: (value: number) => void;
  };
  isExternalSelected: boolean | null;
  onCreateProject: () => void;
  onClickPartnerModal: () => void;
  onClickPatientModal: () => void;
  onClickRemakeModal: () => void;
  onClickIndication: (alias: string) => void;
  onGoBack: () => void;
  isProjectCreator: boolean;
}

function CreateProject({
  language,
  numbering,
  teeth,
  tooth,
  copyData,
  bridge,
  toothSvgRef,
  projectNameRef,
  teethContextActions,
  toothShade,
  indicationInfo,
  indicationFormat,
  indicationOption,
  scanMode,
  scanModeListCt,
  isClinicUser,
  pageType,
  restorationPontic,
  occlusalSurface,
  ceramicMetal,
  isValidInformationValue,
  isValidIndicationValue,
  isValidClient,
  //
  projectPartners,
  hasMoreProjectPartners,
  employeeList,
  caseId,
  informationControl,
  informationErrors,
  setInformation,
  informationWatch,
  remakeTypeList,
  remake,
  remakeSwitch,
  isClientError,
  clientName,
  clientGroupCode,
  isPatientError,
  patientName,
  patientCode,
  projectPatients,
  hasMoreProjectPatients,
  projectPatientsPending,
  isMultidie,
  isExternalSelected,
  onCreateProject,
  onFetchProjectPatients,
  onSearchProjectPatients,
  onFetchProjectPartners,
  onClickIndication,
  onClickPartnerModal,
  onClickPatientModal,
  onClickRemakeModal,
  onGoBack,
  isProjectCreator,
}: CreateProjectInfoProps) {
  const { t } = useTranslation();
  const projectTitleList = [
    {
      pageType: 'create',
      pageTitle: 'New Project',
      code: 'PROJECT_NEW_TITLE',
    },
    {
      pageType: 'edit',
      pageTitle: 'Edit Project',
      code: 'PROJECT_EDIT_TITLE',
    },
    {
      pageType: 'copy',
      pageTitle: 'Copy Project',
      code: 'PROJECT_COPY_TITLE',
    },
  ];
  const pageTitle = projectTitleList.find(item => item.pageType === pageType)?.code;

  return (
    <StyledCreateProjectInfo>
      <PageTitleGoBack title={t(`${pageTitle}`)} goBack={onGoBack} />

      <div className="projectInfo__project_name_box">
        <div className="createProject__input_project">
          {pageType !== 'edit' && (
            <div className="project_name_title">
              <CustomText fontSize={20} fontWeight={500} marginLeft={10}>
                <T>PROJECT_NAME</T>
              </CustomText>
              <CustomText fontSize={16} fontWeight={500} fontColor="#d40000">
                *
              </CustomText>
            </div>
          )}
          <Controller
            name="projectTitle"
            control={informationControl}
            defaultValue=""
            rules={{ required: !isClinicUser }}
            render={({ onChange, value }) => {
              if (pageType === 'edit') {
                return (
                  <CustomText
                    className="project_name_text"
                    fontSize={20}
                    fontWeight={500}
                    marginRight={5}
                  >
                    {value}
                  </CustomText>
                );
              } else {
                return (
                  <div className="title__wrapper">
                    <MuiWrapper className="form__input_box project_name">
                      <TextField
                        className="form__input_box"
                        inputRef={projectNameRef}
                        variant="outlined"
                        fullWidth
                        placeholder={t('PLACEHOLDER_PROJECT_NAME')}
                        error={informationErrors.projectTitle?.type === 'required'}
                        value={value}
                        onChange={e => {
                          if (e.target.value.length <= 20 && !!regProjectName(e.target.value)) {
                            // setProjectNameLength(e.target.value.length);
                            onChange(e);
                          } else {
                            return;
                          }
                        }}
                      />
                    </MuiWrapper>
                  </div>
                );
              }
            }}
          />
          <div className="project_name_date">
            <CustomText fontColor={color.gray_b5} fontSize={13}>
              {language === 'en'
                ? moment().locale(language).format('MMM. DD. YYYY')
                : moment().locale(language).format('YYYY. MM. DD')}
            </CustomText>
          </div>
        </div>
        {pageType === 'create' && (
          <div className="createProject__switch_box">
            <CustomText fontSize={16} fontWeight={400}>
              <T>MULTIDIE_TITLE</T>
            </CustomText>
            <MuiWrapper>
              <Switch
                color="primary"
                value={isMultidie.value}
                checked={!!isMultidie.value}
                className="createProject__switch"
                onChange={() => {
                  isMultidie.value === 0 ? isMultidie.setValue(1) : isMultidie.setValue(0);
                }}
              />
            </MuiWrapper>
          </div>
        )}
      </div>

      <div className="projectInfo__project_deail_box">
        <CreateProjectInfo
          language={language}
          isClinicUser={isClinicUser}
          pageType={pageType}
          projectPartners={projectPartners}
          hasMoreProjectPartners={hasMoreProjectPartners}
          employeeList={employeeList}
          caseId={caseId}
          informationControl={informationControl}
          informationErrors={informationErrors}
          informationWatch={informationWatch}
          remakeTypeList={remakeTypeList}
          remake={remake}
          isClientError={isClientError}
          clientName={clientName}
          isPatientError={isPatientError}
          patientName={patientName}
          patientCode={patientCode}
          projectPatients={projectPatients}
          hasMoreProjectPatients={hasMoreProjectPatients}
          projectPatientsPending={projectPatientsPending}
          restorationPontic={restorationPontic}
          occlusalSurface={occlusalSurface}
          ceramicMetal={ceramicMetal}
          isExternalSelected={isExternalSelected}
          onFetchProjectPatients={onFetchProjectPatients}
          onSearchProjectPatients={onSearchProjectPatients}
          onFetchProjectPartners={onFetchProjectPartners}
          onClickPartnerModal={onClickPartnerModal}
          onClickPatientModal={onClickPatientModal}
          onClickRemakeModal={onClickRemakeModal}
          isProjectCreator={isProjectCreator}
        />
        {useMemo(() => {
          if (!indicationFormat) return;
          if (!indicationInfo) return;

          return (
            <CreateProjectIndicationContainer
              numbering={numbering}
              teeth={teeth}
              tooth={tooth}
              bridge={bridge}
              copyData={copyData}
              toothSvgRef={toothSvgRef}
              teethContextActions={teethContextActions}
              toothShade={toothShade}
              indicationFormat={indicationFormat}
              indicationInfo={indicationInfo}
              indicationOption={indicationOption}
              isMultidie={isMultidie}
              scanMode={scanMode}
              scanModeListCt={scanModeListCt}
              restorationPontic={restorationPontic}
              occlusalSurface={occlusalSurface}
              ceramicMetal={ceramicMetal}
              isVaildTeethViewer={isValidClient}
              clientGroupCode={clientGroupCode}
              onClickIndication={onClickIndication}
              isProjectCreator={isProjectCreator}
            />
          );
        }, [
          numbering,
          teeth,
          tooth,
          bridge,
          copyData,
          toothSvgRef,
          teethContextActions,
          toothShade,
          indicationFormat,
          indicationInfo,
          indicationOption,
          isMultidie,
          scanMode,
          scanModeListCt,
          restorationPontic,
          occlusalSurface,
          ceramicMetal,
          isValidClient,
          onClickIndication,
        ])}
      </div>

      <div className="projectInfo__project_button_box">
        <MuiButton
          disableElevation
          variant="outlined"
          className="projectInfo__project_button_item"
          onClick={onGoBack}
        >
          <T>GLOBAL_BACK</T>
        </MuiButton>
        <MuiButton
          disableElevation
          disabled={!isValidInformationValue}
          variant="contained"
          className="projectInfo__project_button_item"
          onClick={onCreateProject}
        >
          <T>GLOBAL_SAVE</T>
        </MuiButton>
      </div>
    </StyledCreateProjectInfo>
  );
}

const StyledCreateProjectInfo = styled.section<{}>`
  .projectInfo__project_name_box {
    display: flex;
    align-items: center;
    justify-content: space-between;
    /* column-gap: 20px; */
    height: 40px;
    margin-bottom: 20px;
    .createProject__input_project {
      align-items: center;
      display: flex;
      height: 100%;
      .project_name_title {
        display: flex;
        column-gap: 5px;
        margin-right: 42px;
      }
      .project_name_text {
        height: 100%;
        display: flex;
        align-items: flex-end;
      }
      .form__input_box {
        width: 740px;
        height: 46px;
        &.project_name {
          margin-right: 20px;
        }
      }
      .project_name_date {
        height: 100%;
        display: flex;
        align-items: flex-end;
      }
    }

    .createProject__switch_box {
      display: flex;
      align-items: center;
      column-gap: 15px;
      .createProject__switch {
        padding: 7px;
        left: -7px;
        .MuiSwitch-track {
          border-radius: 12px;
        }
        .MuiSwitch-thumb {
          background-color: #ffffff;
        }
      }
    }
  }
  .projectInfo__project_deail_box {
    display: flex;
    width: 100%;
    height: 768px;
    padding: 30px;
    background-color: ${color.white};
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.16);
    border-radius: 7px;
    margin-bottom: 20px;
    /* flex: 3; */
    /* column-gap: 60px; */
    /* column-gap: 90px; */
  }
  .projectInfo__project_button_box {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 10px;
    margin-bottom: 20px;
    .projectInfo__project_button_item {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 200px;
      height: 45px;
      padding: 0 15px;
      /* background-color: ${color.blue}; */
      border-radius: 3px;
      color: white;
    }
  }
`;

export default CreateProject;
